import type { ShallowRef } from "vue"

import type { File } from "~/models/polyschema/File"

import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendFilePropertiesRequests } from "~/classes/handlers/file/properties"

import { PropertyId } from "../entity"
import { withReactive } from "../entity/ReactiveEntity"
import { sendFolderListRequests } from "../handlers/file/list"
import { sendSearchListRequests } from "../handlers/search/list"
import { StaticSearch } from "../search/StaticSearch"
import { BaseFile } from "./BaseFile"

export interface ReactiveFileOptions {
  /**
   * If true, the file properties will be requested immediately after the component is mounted
   * @default false
   */
  immediate?: boolean
  /**
   * If true, the file properties will not be requested from the server, but updates will
   * still be integrated via events
   * @default false
   */
  noRequest?: boolean

  /**
   * If true, the file will not be registered for lifecycle events (mount/unmount)
   * @default false
   */
  noLifecycle?: boolean
}

export class ReactiveFile extends withReactive<
  FileProperty,
  File,
  StaticFile,
  typeof BaseFile
>(BaseFile) {
  _inner: ShallowRef<StaticFile> | WritableComputedRef<StaticFile>
  _innerId: string
  isSetup = false
  isDisposed = false
  options?: ReactiveFileOptions
  fetchSelection: InterfaceSelection
  location: SelectionLocation
  pathSelection: InterfaceSelection

  /**
   * The selections are only used in "immediate" mode. We currently don't automatically
   * check whether the file has the requested selection in non-immediate mode.
   */
  constructor({ file, selection, pathSelection, location, options }: {
    file: Ref<StaticFile>
    selection?: InterfaceSelection
    pathSelection?: InterfaceSelection
    location: SelectionLocation
    options?: ReactiveFileOptions
  }) {
    super()
    this._inner = file
    this._innerId = file.value.id
    this.fetchSelection = selection ?? InterfaceSelection.minimumPropertiesViewSelection()
    this.pathSelection = pathSelection ?? InterfaceSelection.minimumPathSelection()
    this.location = location
    this.options = options

    // in immediate mode we have to register the callbacks immediately
    if (this.options?.immediate)
      this.use()

    if (!this.options?.noLifecycle) {
      onMounted(this.use)
      onUnmounted(this.dispose)
    }
  }

  static fromFileId(
    fileId: string,
    selection: InterfaceSelection,
    location: SelectionLocation,
    options: ReactiveFileOptions,
  ): ReactiveFile {
    return new ReactiveFile({
      file: shallowRef(StaticFile.blank(fileId)),
      selection,
      location,
      options,
    })
  }

  intoStatic = () => new StaticFile(this.inner())
  inner = () => this._inner.value.inner()

  override path<T = StaticFile>(): T[] | undefined {
    return this.asStatic().path()
  }

  mergeWithNewer<T = StaticFile>(later?: T): boolean {
    const newer = later as StaticFile | undefined
    if (!newer)
      return false

    const changed = this.asStatic().mergeWithNewer(newer)
    if (changed)
      this._inner.value = this.intoStatic()

    return changed
  }

  override addProperty(property: FileProperty): void {
    const blank = StaticFile.blank(this.inner().id)
    blank.addProperty(property)
    this.mergeWithNewer(blank)
  }

  refresh = () => {
    const { isOffline } = useCache()

    if (this.options?.noRequest)
      return
    if (!this._inner.value)
      throw new Error("Cannot request file properties when file is undefined")

    sendFilePropertiesRequests([{
      requestId: generateWorkerRequestId(),
      fileId: this._inner.value.id,
      offline: isOffline(),
      selection: this.fetchSelection.inner(),
      pathSelection: this.pathSelection.inner(),
    }])
  }

  onFilePropertiesChanged = (file: FileEvent) => {
    if (!this._inner.value)
      this._inner.value = new StaticFile(file)

    if (file.id !== this._inner.value?.id)
      return console.warn("Reactive file received properties for a different file", file, this._inner.value)

    this._inner.value.mergeWithNewer(new StaticFile(file))
    this._inner.value = new StaticFile(this.inner()) // ensure reactivity is notified
  }

  override updateProperty = <Req = FileUpdateRequestDatagram>(
    propertyId: PropertyId,
    newVal: EntityPropertyValue,
    deleted?: boolean,
    unversioned?: boolean,
    timestamp?: string,
  ): {
    updated: StaticFile
    request: Req
  } => {
    return this.updateProperties([{
      propertyId,
      newVal,
      deleted,
      unversioned,
      timestamp,
    }]) as {
      updated: StaticFile
      request: Req
    }
  }

  override isAuthorizedToEditProperty = (propertyId?: PropertyId) => {
    const { isAuthorizedToEditProperty } = useAuthorization()
    return isAuthorizedToEditProperty(this.asStatic(), propertyId)
  }

  updateProperties = (requests: {
    propertyId: PropertyId
    newVal: EntityPropertyValue
    deleted?: boolean
    unversioned?: boolean
    timestamp?: string
  }[]): {
    updated: StaticFile
    request: FileUpdateRequestDatagram
  } => {
    const { updated, request } = this.asStatic().updateProperties(requests)

    // This will trigger the ref that is required for reactive updates
    this.mergeWithNewer(updated)

    return { updated, request }
  }

  setFileAccessedIfAllowed = (
    timestamp?: string,
  ): {
    updated: StaticFile
    request: FileUpdateRequestDatagram
  } | undefined => {
    const response = this.asStatic().setFileAccessedIfAllowed(timestamp)
    if (!response)
      return
    const { updated, request } = response

    // This will trigger the ref that is required for reactive updates
    this.mergeWithNewer(updated)

    return { updated, request }
  }

  postComment = (markdown: string) => {
    const response = this.asStatic().postComment(markdown)
    if (!response)
      return
    const { updated, request } = response

    // This will trigger the ref that is required for reactive updates
    this.mergeWithNewer(updated)

    return { updated, request }
  }

  onSoftBlur = () => {
    if (this.isEditing())
      this.finishEditing()
  }

  onHardBlur = () => {
    if (this.isEditing())
      this.finishEditing()
  }

  use = () => {
    if (this.isSetup)
      return this

    this.isSetup = true

    const { addEntityEventListener, addInterfaceEventListener } = useEvents()

    if (this._inner.value) {
      const fileId = this._inner.value.id
      addEntityEventListener("fileProperties", fileId, this.onFilePropertiesChanged)
      addEntityEventListener("requestedPropertyEdit", fileId, this.onRequestedPropertyEdit)
      addInterfaceEventListener("afterSoftBlur", this.onSoftBlur)
      addInterfaceEventListener("afterHardBlur", this.onHardBlur)
    }

    if (this.options?.immediate)
      this.refresh()

    return this
  }

  dispose = () => {
    const { removeEntityEventListener, removeInterfaceEventListener } = useEvents()

    if (this.isDisposed)
      return console.error("Reactive file was already disposed")
    this.isDisposed = true

    removeEntityEventListener("fileProperties", this._innerId, this.onFilePropertiesChanged)
    removeEntityEventListener("requestedPropertyEdit", this._innerId, this.onRequestedPropertyEdit)
    removeInterfaceEventListener("afterSoftBlur", this.onSoftBlur)
    removeInterfaceEventListener("afterHardBlur", this.onHardBlur)
  }

  static createReactiveFolderList(
    folderId: string,
    location: SelectionLocation,
    selection: InterfaceSelection,
    pathSelection: InterfaceSelection = InterfaceSelection.minimumPathSelection(),
    sort: PropertyId = PropertyId.FILE_CREATED_AT,
    desc: boolean = true,
    pageSize: number = 50,
  ) {
    const { addEntityEventListener, removeEntityEventListener } = useEvents()

    const files = shallowRef<Record<string, ReactiveFile>>({})

    const addFile = (file: FileEvent) => {
      if (files.value[file.id])
        return
      const reactiveFile = new ReactiveFile({
        file: ref(new StaticFile(file)),
        selection,
        pathSelection,
        location,
        options: { immediate: true, noLifecycle: true },
      })
      reactiveFile.use()
      files.value[file.id] = reactiveFile
      triggerRef(files)
    }
    const removeFile = (file: FileEvent) => {
      if (!files.value[file.id])
        return
      files.value[file.id].dispose()
      delete files.value[file.id]
      triggerRef(files)
    }

    const onChildCreated = (file: FileEvent) => {
      console.debug("ReactiveFolderList: onChildCreated", file)
      addFile(file)
    }
    const onChildProperties = (file: FileEvent) => {
      console.debug("ReactiveFolderList: onChildProperties", file)
      addFile(file)
    }
    const onChildDeleted = (file: FileEvent) => {
      console.debug("ReactiveFolderList: onChildDeleted", file)
      removeFile(file)
    }
    const onChildRemoved = (file: FileEvent) => {
      console.debug("ReactiveFolderList: onChildRemoved", file)
      removeFile(file)
    }

    const unwatch = () => {
      removeEntityEventListener("childCreated", folderId, onChildCreated)
      removeEntityEventListener("childProperties", folderId, onChildProperties)
      removeEntityEventListener("childDeleted", folderId, onChildDeleted)
      removeEntityEventListener("childRemoved", folderId, onChildRemoved)
      for (const fileId in files.value)
        files.value[fileId].dispose()
      files.value = {}
      // don't need to trigger ref here as we're disposing
    }

    // now register the listeners
    addEntityEventListener("childCreated", folderId, onChildCreated)
    addEntityEventListener("childProperties", folderId, onChildProperties)
    addEntityEventListener("childDeleted", folderId, onChildDeleted)
    addEntityEventListener("childRemoved", folderId, onChildRemoved)

    // now make the actual folder list request
    const request: FolderListRequestDatagram = {
      requestId: generateWorkerRequestId(),
      folder: StaticFile.blank(folderId).inner(),
      selection: selection.inner(),
      pathSelection: pathSelection.inner(),
      pageSize,
      sort,
      desc,
      offline: false,
    }
    sendFolderListRequests([request])

    return {
      unwatch,
      files,
      request,
    }
  }

  /**
   * @returns A map of file id to reactive files that are returned from the search. Each file
   * contains a property that indicates its search relevance, which is used for sorting
   */
  static createReactiveSearchList(
    searchId: string,
    location: SelectionLocation,
    selection: InterfaceSelection,
    pathSelection: InterfaceSelection = InterfaceSelection.minimumPathSelection(),
    limit: number = 50,
  ) {
    const { addEntityEventListener, removeEntityEventListener, addInterfaceEventListener, removeInterfaceEventListener } = useEvents()

    // file id to file
    const files = shallowRef<Record<string, ReactiveFile>>({})

    // whether the search has returned yet
    const loaded = ref(false)
    const errored = ref(false)

    const onSearchExecuted = (response: SearchListPageResponseDatagram) => {
      console.debug("ReactiveSearchList: onSearchExecuted", response)
      loaded.value = true
      if (response.error)
        errored.value = true
    }

    const addFile = (file: FileEvent) => {
      if (files.value[file.id])
        return
      const reactiveFile = new ReactiveFile({
        file: ref(new StaticFile(file)),
        selection,
        pathSelection,
        location,
        // we received the properties already from the search list, so don't re-fetch
        options: { immediate: false, noLifecycle: true },
      })
      reactiveFile.use()
      files.value[file.id] = reactiveFile
      triggerRef(files)
    }

    // TODO: How do we remove items from the search?
    const _removeFile = (file: FileEvent) => {
      if (!files.value[file.id])
        return
      files.value[file.id].dispose()
      delete files.value[file.id]
      triggerRef(files)
    }

    const onSearchResult = (file: FileEvent) => {
      console.debug("ReactiveSearchList: onSearchResult", file)
      addFile(file)
    }

    const unwatch = () => {
      removeEntityEventListener("searchResult", searchId, onSearchResult)
      removeInterfaceEventListener("searchListPageResponse", onSearchExecuted)
      for (const fileId in files.value)
        files.value[fileId].dispose()
      files.value = {}
      // don't need to trigger ref here as we're disposing
    }

    // now register the listeners
    addEntityEventListener("searchResult", searchId, onSearchResult)
    addInterfaceEventListener("searchListPageResponse", onSearchExecuted)

    // now make the actual folder list request
    const request: SearchListRequestDatagram = {
      requestId: generateWorkerRequestId(),
      offset: 0,
      search: StaticSearch.blank(searchId).inner(),
      pageSize: limit,
      limit,
      fileSelection: selection.inner(),
      pathSelection: pathSelection.inner(),
      offline: false,
    }
    sendSearchListRequests([request])

    return {
      unwatch,
      files,
      loaded,
      errored,
      request,
    }
  }
}
